import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp } from 'lucide-react';
import { Card } from '../components/UI';
import { analyticsAPI } from '../api/apiClient';
import toast from 'react-hot-toast';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

/**
 * Analytics Page
 */
const AnalyticsPage = () => {
  const [userGrowth, setUserGrowth] = useState([]);
  const [devicesByCountry, setDevicesByCountry] = useState([]);
  const [devicesByOS, setDevicesByOS] = useState([]);
  const [apiPerformance, setApiPerformance] = useState([]);
  const [topErrors, setTopErrors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const [growth, country, os, perf, errors] = await Promise.all([
        analyticsAPI.getUserGrowth(30),
        analyticsAPI.getDevicesByCountry(),
        analyticsAPI.getDevicesByOS(),
        analyticsAPI.getAPIPerformance(7),
        analyticsAPI.getTopErrors(10),
      ]);

      setUserGrowth(growth.data.data);
      setDevicesByCountry(country.data.data);
      setDevicesByOS(os.data.data);
      setApiPerformance(perf.data.data);
      setTopErrors(errors.data.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const COLORS = ['#8b5cf6', '#ec4899', '#06b6d4', '#10b981', '#f59e0b'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
          <BarChart3 size={32} className="text-purple-400" />
          <span>Analytics</span>
        </h1>
        <p className="text-gray-400 mt-2">System statistics and performance metrics</p>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth */}
        {userGrowth.length > 0 && (
          <Card>
            <h3 className="text-lg font-bold text-white mb-4">User Growth (30 days)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={userGrowth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="_id" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#8b5cf6"
                  dot={{ fill: '#8b5cf6' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* API Performance */}
        {apiPerformance.length > 0 && (
          <Card>
            <h3 className="text-lg font-bold text-white mb-4">API Performance (7 days)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={apiPerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="_id" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="avgResponseTime"
                  stroke="#10b981"
                  name="Avg Response (ms)"
                />
                <Line
                  type="monotone"
                  dataKey="totalRequests"
                  stroke="#8b5cf6"
                  name="Total Requests"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Devices by Country */}
        {devicesByCountry.length > 0 && (
          <Card>
            <h3 className="text-lg font-bold text-white mb-4">Top Countries</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={devicesByCountry.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="_id" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                  }}
                />
                <Bar dataKey="count" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Devices by OS */}
        {devicesByOS.length > 0 && (
          <Card>
            <h3 className="text-lg font-bold text-white mb-4">Device OS Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={devicesByOS}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ _id, count }) => `${_id}: ${count}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {devicesByOS.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Top Errors */}
      {topErrors.length > 0 && (
        <Card>
          <h3 className="text-lg font-bold text-white mb-4">Top Errors</h3>
          <div className="space-y-3">
            {topErrors.map((error, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition"
              >
                <div className="flex-1">
                  <p className="text-white font-medium truncate">{error._id}</p>
                </div>
                <div className="ml-4 text-right">
                  <p className="text-red-400 font-bold">{error.count} occurrences</p>
                  <p className="text-gray-400 text-sm">
                    Last: {new Date(error.lastOccurred).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsPage;
